/* eslint-disable max-statements */
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { defineStore } from 'pinia'

//Types
import type {
  ApiPupilScreener,
  PupilScreener,
  SubtestEntity,
  QuestionEntity,
  PupilAnswer,
  InputMethod,
  QuestionsArrayPropertyName,
  OptionsEntity,
  ManagedTimeout
} from '@/customTypes'
import type { Ref } from 'vue'

// Services
import FirebaseInstance from '@/services/FirebaseInstance.js'
import AxiosInstance from '@/services/AxiosInstance.js'
import Logger from '@/services/Logger.js'
import FullScreen from '@/services/FullScreen.js'

// Helpers
import { convertSnakeToCamelCase, useTimeout } from '@/utils/helpers'

// Stores
import { useEffectsStore } from '@/stores/EffectsStore'
import { useDevStore } from './DevStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'
import { usePreloadStore } from './PreloadStore'
import { useServiceHealthStore } from '@/stores/ServiceHealthStore'

const logger: any = new Logger('DataStore', true)

export const useDataStore = defineStore('data', () => {
  const effectsStore = useEffectsStore()
  const DEV_STORE = useDevStore()
  const activeScreenerStore = useActiveScreenerStore()
  const preloadStore = usePreloadStore()
  const serviceHealthStore = useServiceHealthStore()
  const { setManagedTimeout, clearManagedTimeout } = useTimeout()

  const screenerData: Ref<PupilScreener | null> = ref(null)
  const screenerCompleted: Ref<boolean> = ref(false)
  const screenerTemplateId: Ref<number | null> = ref(null)
  const screenerAvatar: Ref<string | null> = ref(null)
  const pupilQuestions: Ref<QuestionEntity[] | null> = ref(null)
  const questionsProperty: Ref<QuestionsArrayPropertyName> = ref('practiceQuestions')
  const pupilAnswers: Ref<PupilAnswer[]> = ref([])
  const stars: Ref<number> = ref(0)
  const screenerIntroPlayed: Ref<boolean> = ref(false)

  const screenerError: Ref<{
    type: string
    message: string
    metadata?: {
      serviceUrl?: string
      [key: string]: any
    }
  } | null> = ref(null)
  const fullscreenElement = ref(null)

  const currentSubtest: Ref<SubtestEntity | null> = ref(null)
  const currentQuestion: Ref<QuestionEntity | null> = ref(null)
  const currentQuestionTime: Ref<number> = ref(0)
  const pausedState: Ref<boolean> = ref(false)
  const demoProgressKey: Ref<number> = ref(0)
  const demoAvatarKey: Ref<number> = ref(0)
  const demoBackgroundKey: Ref<number> = ref(0)
  const demoLoading: Ref<boolean> = ref(false)

  const screenerInProgress: Ref<boolean> = ref(false)

  const questionAttempts: Ref<number> = ref(1)

  const extendedTimeMultiplier: Ref<number> = ref(1)
  const answerTimeMultiplier = computed(() => extendedTimeMultiplier.value || 1)
  const effectiveWaitTime = computed(
    () => (currentQuestion.value?.waitTime ?? 0) * answerTimeMultiplier.value
  )

  //Volume Levels
  const avatarVolumeLevel: Ref<number> = ref(1)
  const backgroundVolumeLevel: Ref<number> = ref(0.3)
  const backgroundVolumeFadeLevel: Ref<number> = ref(0.1)

  const isOffline: Ref<boolean> = ref(false)
  const loadErrorTimeouts = ref<Map<string, ManagedTimeout>>(new Map())

  // Get the current subtest questions length
  const currentSubtestQuestionsLength: Ref<number> = computed(() => {
    return currentSubtest.value?.questions?.length || 0
  })

  // Get answers pupil answers for the current subtest
  const currentSubtestAnswers: Ref<PupilAnswer[] | []> = computed(() => {
    return [...pupilAnswers.value]
      .filter((answer: PupilAnswer) => answer.subtestIndex === currentSubtest.value?.index)
      .sort((answerA, answerB) => answerB.questionIndex - answerA.questionIndex)
  })

  onMounted(() => {
    window.addEventListener('online', updateNetworkStatus)
    window.addEventListener('offline', updateNetworkStatus)
  })

  onUnmounted(() => {
    window.removeEventListener('online', updateNetworkStatus)
    window.removeEventListener('offline', updateNetworkStatus)
  })

  async function regenerateCode(pupilScreenerId: Number, expiredCode: string): Promise<void> {
    const response = AxiosInstance.get(
      `${_API_BASE_URL_}/screening/screener/${pupilScreenerId}/regenerate/${expiredCode}`,
      {}
    )

    return response
  }

  /**
   * Set the current subtest
   * @param subtestIndex
   */
  function getNextSubtest(subtestIndex: number): SubtestEntity | null {
    return (
      // Find the subtest with the matching index
      screenerData.value?.pupilQuestionsTemplate?.find(
        (subtest) => subtest.index === subtestIndex
      ) ||
      // If no subtest is found, find the next subtest with a higher index
      screenerData.value?.pupilQuestionsTemplate
        ?.filter((subtest) => subtest.index > subtestIndex)
        .sort((a, b) => a.index - b.index)[0] ||
      // else return null
      null
    )
  }

  /**
   * Set the screener data
   * @param {ApiPupilScreener} apiData
   */
  async function setScreenerData(apiData: ApiPupilScreener): Promise<void> {
    if (!apiData) return

    const storeData = convertSnakeToCamelCase(apiData) as PupilScreener

    screenerData.value = storeData
    extendedTimeMultiplier.value = storeData.extendedTimeMultiplier ?? 1
    setScreenerCompleted(storeData.pupilQuestionsCompleted)
    if (storeData.pupilQuestionsCompleted) {
      activeScreenerStore.completedScreener()
    }
    pupilAnswers.value = DEV_STORE.ignoreApiAnswers ? [] : storeData.pupilAnswers || []
    screenerTemplateId.value = storeData.screenerTemplateId
    avatarVolumeLevel.value = storeData.metadata?.avatarVolume || avatarVolumeLevel.value
    backgroundVolumeLevel.value = storeData.metadata?.backgroundVolume
    backgroundVolumeFadeLevel.value = storeData.metadata?.backgroundFadeVolume
    stars.value = storeData?.rewardCount

    if (storeData.metadata?.animationTimingFile) {
      await fetchAnimationTimings(storeData.metadata?.animationTimingFile)
      effectsStore.setPromptAnimation(storeData.metadata?.promptAnimation)
      effectsStore.setDefaultIdleAnimations(storeData.metadata?.idleAnimationSet || [])
      effectsStore.setTransitionAnimation(storeData.metadata?.transitionAnimationSet || [])
    }

    if (storeData.metadata?.avatar) {
      const baseUrl = `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}`
      const response = await fetch(
        `${baseUrl}/avatar/${storeData.metadata?.avatar}?v=${_ASSETS_VERSION_}`
      )
      if (!response.ok) return
      screenerAvatar.value = await response.json()
    }

    const startFromLastQuestion =
      DEV_STORE.ignoreApiAnswers !== true &&
      storeData.pupilAnswers?.length &&
      !storeData.pupilQuestionsCompleted

    if (startFromLastQuestion) {
      startScreenerFromNextQuestion()
    } else {
      const subtestIndex = DEV_STORE.ignoreApiAnswers ? DEV_STORE.activeSubtestIndex : 0
      const questionIndex = DEV_STORE.ignoreApiAnswers ? DEV_STORE.activeQuestionIndex : 0
      const isPracticeQuestion = DEV_STORE.ignoreApiAnswers
        ? DEV_STORE.isPracticeQuestion
        : (storeData.pupilQuestionsTemplate?.[subtestIndex]?.practiceQuestions?.length || 0) > 0
      setCurrentSubtest(subtestIndex)
      //start with the first practice question
      setCurrentQuestion(questionIndex, isPracticeQuestion)
    }
  }

  /**
   * Set the current subtest
   * @param subtestIndex
   */
  function setCurrentSubtest(subtestIndex: number): void {
    currentSubtest.value =
      // Find the subtest with the matching index
      screenerData.value?.pupilQuestionsTemplate?.find(
        (subtest) => subtest.index === subtestIndex
      ) ||
      // If no subtest is found, find the next subtest with a higher index
      screenerData.value?.pupilQuestionsTemplate
        ?.filter((subtest) => subtest.index > subtestIndex)
        .sort((a, b) => a.index - b.index)[0] ||
      // else return null
      null
  }

  /**
   * Set the current question
   * @param questionIndex
   * @param isPractice practice questions are stored in a different separately
   */
  function setCurrentQuestion(questionIndex: number, isPractice: boolean): void {
    questionsProperty.value = isPractice ? 'practiceQuestions' : 'questions'
    currentQuestion.value =
      // Find the question with the matching index
      currentSubtest.value?.[questionsProperty.value]?.find(
        (question) => question.index === questionIndex
      ) ||
      // If no question is found, find the next question with a higher index
      currentSubtest.value?.[questionsProperty.value]
        ?.filter((question) => question.index > questionIndex)
        .sort((a, b) => a.index - b.index)[0] ||
      // else return null
      null
    currentQuestion.value!.completed = false
    syncQuestionTextToFirebase()
  }

  /* Title of the question the pupil will see after the current one, or null at the end. */
  function getNextQuestionText(): string | null {
    if (!currentSubtest.value || !currentQuestion.value) {
      return null
    }

    const subtest = currentSubtest.value
    const question = currentQuestion.value
    const isPractice = question.isPracticeQuestion
    const isLastQuestion = checkIfLastQuestion(question, subtest, isPractice)
    const isLastSubtest = checkIfLastSubtest(subtest)

    if (isPractice) {
      if (isLastQuestion) {
        return subtest.questions?.find((item) => item.index === 0)?.title ?? null
      }
      return (
        subtest.practiceQuestions
          ?.filter((item) => item.index > question.index)
          .sort((first, second) => first.index - second.index)[0]?.title ?? null
      )
    }

    if (isLastQuestion) {
      if (isLastSubtest) {
        return null
      }
      const nextSubtest = getNextSubtest(subtest.index + 1)
      return (
        nextSubtest?.practiceQuestions?.find((item) => item.index === 0)?.title ??
        nextSubtest?.questions?.find((item) => item.index === 0)?.title ??
        null
      )
    }

    return (
      subtest.questions
        ?.filter((item) => item.index > question.index)
        .sort((first, second) => first.index - second.index)[0]?.title ?? null
    )
  }

  /**
   * Push the current + next question titles to the teacher tracker via Firebase
   * so a teacher can repeat the prompt word for word.
   */
  function syncQuestionTextToFirebase(): void {
    if (!currentQuestion.value || !currentSubtest.value) {
      return
    }
    activeScreenerStore.updateFirebase({
      currentQuestionId: currentQuestion.value.id,
      currentQuestionText: currentQuestion.value.title,
      nextQuestionText: getNextQuestionText(),
      currentSubtestIndex: currentSubtest.value.index,
      currentQuestionIndex: currentQuestion.value.index
    })
  }

  function completedCurrentQuestion() {
    if (currentQuestion.value) {
      currentQuestion.value.completed = true
    }
  }
  function setScreenerError(
    type: string,
    error: string,
    metadata?: {
      serviceUrl?: string
      [key: string]: any
    }
  ) {
    activeScreenerStore.sendScreenerError({ type, message: error, priority: 3, metadata })
    screenerError.value = { type: type, message: error, metadata }
  }

  /**
   * Handle the audio answer given by the pupil
   * @param answer
   * @param soundRecorded
   */
  async function audioAnswerGiven(
    answer: Blob,
    soundRecorded: boolean,
    inputDuration: number,
    liveSttDetectedWords: boolean
  ): Promise<void> {
    if (!currentSubtest.value || !currentQuestion.value) return
    logger.info('audioAnswerGiven', answer, soundRecorded)

    // Check if the audio blob contains zero bytes
    if (answer.size === 0) {
      return
    }

    // Send answer to the API
    const formData = new FormData()
    const answerData = {
      questionId: currentQuestion.value!.id,
      questionIndex: currentQuestion.value!.index,
      questionTitle: currentQuestion.value!.title,
      subtestId: currentSubtest.value!.id,
      subtestIndex: currentSubtest.value!.index,
      practiceAnswer: currentQuestion.value!.isPracticeQuestion,
      soundRecorded,
      liveSttDetectedWords,
      rewardCount: 1
    }

    formData.append('audioFile', answer)
    formData.append('answerData', JSON.stringify(answerData))
    formData.append('sampleDuration', String(inputDuration))

    try {
      //Note: Comment out to stop it from saving in the database
      await sendAnswerToApi(formData)
      pupilAnswers.value?.push(answerData)
    } catch (error) {
      setScreenerError(
        'AudioAnswerError',
        'Sorry, there was an error saving the users answer. Please refresh the page and try again. \n If the problem persists, please contact the support team.'
      )
      logger.error('Error sending audio answer to API', error)
    }
  }

  /**
   * Handle the selection of an answer
   * @param answer
   */
  async function onSelectAnswer(answer: OptionsEntity | null): Promise<void> {
    if (!currentSubtest.value || !currentQuestion.value) return

    logger.info('onSelectAnswer', answer)
    // Send answer to the API
    const formData = new FormData()
    const answerData = {
      questionId: currentQuestion.value!.id,
      questionIndex: currentQuestion.value!.index,
      subtestId: currentSubtest.value!.id,
      subtestIndex: currentSubtest.value!.index,
      practiceAnswer: currentQuestion.value!.isPracticeQuestion,
      selection: answer,
      rewardCount: 1
    }
    formData.append('answerData', JSON.stringify(answerData))

    try {
      await sendAnswerToApi(formData)
      pupilAnswers.value?.push(answerData)
    } catch (error) {
      setScreenerError(
        'Answer Error',
        'Sorry, there was an error saving the users answer. Please refresh the page and try again. \n If the problem persists, please contact the support team.'
      )
      logger.error('Error sending audio answer to API', error)
    }
  }

  function setQuestionAttempts(value: number) {
    questionAttempts.value = value
  }

  /**
   * Send answer data to the API
   * @param data
   */
  async function sendAnswerToApi(data: FormData): Promise<void> {
    completedCurrentQuestion()
    if (DEV_STORE.sendAnswerToApi === false) return
    logger.info('sendAnswerToApi ')
    return await AxiosInstance.put(
      `${_API_BASE_URL_}/screening/pupils/${screenerData.value!.pupilId}/screeners/${screenerData.value!.classScreenerId}/${screenerData.value!.id}/pupil-answers`,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  }

  async function sendRewardUpdateToApi(quantity: number) {
    if (DEV_STORE.sendAnswerToApi === false || !screenerData.value!.id) return
    logger.info('sendRewardToApi ')
    return await AxiosInstance.put(
      `${_API_BASE_URL_}/screening/pupils/${screenerData.value!.pupilId}/screeners/${screenerData.value!.classScreenerId}/${screenerData.value!.id}/add-reward`,
      { rewardCount: quantity }
    )
  }

  /**
   * Set the next question based on the current question
   */
  function callSetNextQuestion() {
    setNextQuestion(
      currentQuestion.value!.index,
      currentQuestion.value!.isPracticeQuestion,
      currentSubtest.value!.index
    )
  }

  /**
   * Set the next question based on the current question
   * @param currentQuestionIndex
   * @param isPracticeQuestion
   * @param currentSubtestIndex
   */
  function setNextQuestion(
    currentQuestionIndex: number,
    isPracticeQuestion: boolean,
    currentSubtestIndex: number
  ) {
    setQuestionAttempts(1)
    logger.log('setNextQuestion()')
    const questionType = isPracticeQuestion ? 'practiceQuestions' : 'questions'
    const currentSubtest = screenerData.value!.pupilQuestionsTemplate!.find(
      (subtest) => subtest.index === currentSubtestIndex
    )

    if (!currentSubtest) throw new Error('Subtest not found')

    const currentQuestion = currentSubtest![questionType]!.find(
      (question) => question.index === currentQuestionIndex
    )

    if (!currentQuestion)
      throw new Error('Unable to find the next question, Please contact the support team.')

    const isLastSubtest: boolean = checkIfLastSubtest(currentSubtest)
    const isLastQuestion: boolean = checkIfLastQuestion(
      currentQuestion,
      currentSubtest,
      isPracticeQuestion
    )

    if (isPracticeQuestion) {
      // If last question was practice question
      if (isLastQuestion) {
        // if it was the last practice question, move the the non-practice questions in the current subtest
        setCurrentSubtest(currentSubtestIndex)
        setCurrentQuestion(0, false)
      } else {
        // else move to the next practice question
        setCurrentSubtest(currentSubtestIndex)
        setCurrentQuestion(currentQuestion.index + 1, true)
      }
    } else {
      // If last question was non-practice question
      if (isLastQuestion && !isLastSubtest) {
        // if it was the last non-practice question and not the last subtest, move to the next subtests practice questions
        setCurrentSubtest(currentSubtestIndex + 1)
        setCurrentQuestion(0, true)
      } else if (!isLastQuestion) {
        // else move to the next non-practice question
        setCurrentSubtest(currentSubtestIndex)
        setCurrentQuestion(currentQuestion.index + 1, false)
      } else {
        // else the screener is completed
        setScreenerCompleted(true)
      }
    }
  }

  /**
   * Start the screener from the quest question based on the last answered question
   */
  function startScreenerFromNextQuestion() {
    //Create a copy of the pupilAnswers array
    const answersArray = [...(pupilAnswers.value as Array<PupilAnswer>)]
    // Find the question with the highest subtest index (last subtest answered)
    const currentSubtestIndex =
      answersArray.sort((a, b) => b.subtestIndex - a.subtestIndex)[0].subtestIndex || 0

    // Find the question with the highest questionIndex for the last subtest ( non-practice questions take precedence over practice questions)
    let currentAnswer: PupilAnswer | null = null

    answersArray.forEach((answer: PupilAnswer) => {
      if (answer.subtestIndex !== currentSubtestIndex) return

      if (
        //If question is null
        currentAnswer === null ||
        //Or if the question is a practice question and the current answer is a practice question and the question index is higher
        (answer.practiceAnswer &&
          currentAnswer.practiceAnswer &&
          answer.questionIndex > currentAnswer.questionIndex) ||
        //Or if the question is not a practice question and the current answer is a practice question
        (!answer.practiceAnswer && currentAnswer.practiceAnswer) ||
        //Or if the question index is higher
        answer.questionIndex > currentAnswer.questionIndex
      ) {
        currentAnswer = answer
      }
    })

    // If for some reason there is no answer start from the last subtest practice question
    if (!currentAnswer) {
      setCurrentSubtest(currentSubtestIndex)
      setCurrentQuestion(0, true)
      return
    }

    currentAnswer = currentAnswer as PupilAnswer // ugly but necessary to avoid TS error

    setNextQuestion(currentAnswer.questionIndex, currentAnswer.practiceAnswer, currentSubtestIndex)
  }

  /**
   * Check if the subtest is the last subtest in the screener
   * @param subtest
   * @returns {boolean}
   */
  function checkIfLastSubtest(subtest: SubtestEntity) {
    return (
      screenerData.value?.pupilQuestionsTemplate?.sort((a, b) => b.index - a.index)[0]?.index ===
      subtest.index
    )
  }

  /**
   *  Check if the question is the last question in the subtest
   *  Compare the question index with the highest index in the subtest
   *  Practice questions are stored in a different array so we need to check if the question is a practice question
   * @param question
   * @param subtest
   * @param isPracticeQuestion
   * @returns {boolean}
   */
  function checkIfLastQuestion(
    question: QuestionEntity,
    subtest: SubtestEntity,
    isPracticeQuestion?: boolean
  ) {
    let questionType

    if (isPracticeQuestion) {
      questionType = 'practiceQuestions'
    } else {
      questionType = question?.isPracticeQuestion ? 'practiceQuestions' : 'questions'
    }

    return (
      subtest[questionType as QuestionsArrayPropertyName]!.sort((a, b) => b.index - a.index)[0]
        ?.index === question!.index
    )
  }

  /**
   * Add a star to the pupil
   */
  function addStar(numberOfStars: number = 1) {
    stars.value += numberOfStars
  }

  /**
   * Login to the API
   */
  const login = async (): Promise<void> => {
    logger.info('Logging in to the Fire API')
    try {
      await FirebaseInstance.auth().signInWithEmailAndPassword(_API_USER_LOGIN_, _API_USER_PASSWORD_)
      serviceHealthStore.firebaseStatus = 'healthy'
    } catch (error) {
      logger.error('Error logging into Firebase:', error)
      serviceHealthStore.firebaseStatus = 'unhealthy'
      throw error // Re-throw the error so the calling component can handle it if needed
    }
  }

  /**
   * Fetches the screener data from the API
   * @param {string} code
   * @param {InputMethod} method - the method in which the code was provided
   * @returns {boolean} - whether the pupil has answered any questions
   */
  async function fetchScreenerData(
    code: string,
    method: InputMethod,
    demo: Boolean
  ): Promise<boolean> {
    const data = await activeScreenerStore.initScreener({ code, method, demo })
    await setScreenerData(data)

    return pupilAnswers.value !== null && pupilAnswers.value.length > 0
  }

  /**
   * Set up the value of the screenerCompleted variable
   * @param value
   */
  function setScreenerCompleted(value: boolean) {
    if (value) {
      activeScreenerStore.completedScreener()
    }
    screenerCompleted.value = value
  }

  /**
   * Fetches animation timing data from the specified file and sets it in the effects store.
   * @param {string} file - The name of the file containing animation timing data specified in screener metadata.
   */
  async function fetchAnimationTimings(file: string): Promise<void> {
    const response = await fetch(
      `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/animations/${file}?v=${_ASSETS_VERSION_}`
    )
    if (!response.ok) return
    const data = await response.json()
    effectsStore.setAnimationTimingData(data.animations)
  }

  const starPopGameActive: Ref<boolean> = ref(false)

  function setStarPopGameState(isActive: boolean) {
    starPopGameActive.value = isActive
  }

  function updateQuestionTimestamp(time: number) {
    currentQuestionTime.value = time
  }

  async function fetchHighlightingTimestamps(fileUrl: string) {
    const fileNameWithJson = fileUrl.split('.')[0] + '.json'
    const response = await fetch(
      `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/avatar-text/${fileNameWithJson}?v=${_ASSETS_VERSION_}`
    )
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    const data = await response.json()
    if (data.words === undefined) {
      return data.map((word: any) => ({ start: word.start, end: word.end, word: word.word }))
    } else {
      return data.words.map((word: any) => ({
        start: word.start,
        end: word.end,
        word: word.word
      }))
    }
  }

  function setScreenerInProgress(value: boolean) {
    screenerInProgress.value = value
  }

  function updateNetworkStatus() {
    isOffline.value = !navigator.onLine
  }

  function setPausedState(paused: boolean) {
    pausedState.value = paused
  }

  function setExtendedTimeMultiplier(value: number): void {
    extendedTimeMultiplier.value = value || 1
  }

  function setScreenerIntroPlayed(value: boolean) {
    screenerIntroPlayed.value = value
  }

  const startLoadErrorTimeout = (event: Event): string | null => {
    const target = event.target as HTMLImageElement | HTMLVideoElement
    let id = target.id
    if (loadErrorTimeouts.value.has(id)) return id
    if (!id) {
      id = `asset-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      target.id = id
    }
    const timeout = setManagedTimeout(() => {
      if (target instanceof HTMLImageElement) {
        logger.error('Error loading image asset:', target.src)
      } else if (target instanceof HTMLVideoElement) {
        if (target.error) {
          logVideoError(target)
        }
      }
      setScreenerError(
        'Asset Loading Error',
        'Something went wrong when loading screener assets. Please check your internet connection and try reloading the page.'
      )
    }, 15000)
    loadErrorTimeouts.value.set(id, timeout)
    return id
  }

  const clearLoadErrorTimeout = (id: string) => {
    const timeout = loadErrorTimeouts.value.get(id)
    if (timeout) {
      clearManagedTimeout(ref(timeout))
      loadErrorTimeouts.value.delete(id)
    }
  }

  const clearAllLoadErrorTimeouts = () => {
    loadErrorTimeouts.value.forEach((timeout) => clearManagedTimeout(ref(timeout)))
    loadErrorTimeouts.value.clear()
  }

  const logVideoError = (target: HTMLVideoElement) => {
    switch (target.error?.code) {
      case MediaError.MEDIA_ERR_ABORTED:
        logger.error('Video loading aborted:', target.src)
        break
      case MediaError.MEDIA_ERR_NETWORK:
        logger.error('Network error while loading video:', target.src)
        break
      case MediaError.MEDIA_ERR_DECODE:
        logger.error('Decoding error while loading video:', target.src)
        break
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
        logger.error('Video format not supported:', target.src)
        break
      default:
        logger.error('Unknown video error:', target.src)
        break
    }
  }

  const demoSkipTo: Ref<string | null> = ref(null)

  const resetDemoSkippingTo = () => {
    demoSkipTo.value = null
  }
  const canSkipQuestion = computed(() => {
    return currentQuestion.value && !isLastQuestionInSubtest.value
  })

  const canMoveToNextSubtest = computed(() => {
    return currentSubtest.value && !isLastSubtest.value
  })

  const canMoveToPreviousSubtest = computed(() => {
    return currentSubtest.value && currentSubtest.value.index > 0
  })

  const isLastQuestionInSubtest = computed(() => {
    if (!currentQuestion.value || !currentSubtest.value) return false
    return checkIfLastQuestion(currentQuestion.value, currentSubtest.value)
  })

  const isLastSubtest = computed(() => {
    if (!currentSubtest.value) return false
    return checkIfLastSubtest(currentSubtest.value)
  })

  function skipQuestion() {
    if (!canSkipQuestion.value) return
    callSetNextQuestion()
    resetQuestion()
  }

  async function moveToNextSubtest() {
    if (!canMoveToNextSubtest.value) return
    if (activeScreenerStore.activeScreener?.section === 'ScreenerIntro') {
      setScreenerIntroPlayed(true)
      resetQuestion()
      nextTick(() => activeScreenerStore.sendSectionChange({ section: 'ScreenerQuestionsView' }))
      return
    }
    const nextSubtest = getNextSubtest(currentSubtest.value!.index + 1)
    if (nextSubtest) {
      activeScreenerStore.sendSectionChange({ section: 'ScreenerQuestionsView' })
      setScreenerIntroPlayed(true)
      setCurrentSubtest(nextSubtest.index)
      setCurrentQuestion(0, true)
      await preloadStore.preloadSubtestById(nextSubtest.index)
      resetQuestion()
    }
  }

  async function moveToPreviousSubtest() {
    if (!canMoveToPreviousSubtest.value) return
    let previousSubtest = null
    // if screener has completed and is no longer on the question screen toggle pause to reload questions view in
    if (
      (screenerCompleted.value &&
        activeScreenerStore.activeScreener?.section === 'StartScreenerView') ||
      activeScreenerStore.activeScreener?.section === 'ScreenerOutro'
    ) {
      setPausedState(true)
      setScreenerCompleted(false)
      previousSubtest = currentSubtest.value
      nextTick(() => {
        setPausedState(false)
      })
    } else {
      previousSubtest = screenerData.value?.pupilQuestionsTemplate?.find(
        (subtest) => subtest.index === currentSubtest.value!.index - 1
      )
    }
    if (previousSubtest) {
      setScreenerIntroPlayed(true)
      setCurrentSubtest(previousSubtest.index)
      setCurrentQuestion(0, true)
      await preloadStore.preloadSubtestById(previousSubtest.index)
      resetQuestion()
    }
  }

  async function playScreenerIntro() {
    demoLoading.value = true
    setPausedState(true)
    effectsStore.resetForScreenerIntro()
    activeScreenerStore.sendSectionChange({ section: 'ScreenerIntro' })
    setCurrentSubtest(0)
    setCurrentQuestion(0, true)
    pupilAnswers.value = []
    stars.value = 0
    screenerIntroPlayed.value = false
    await preloadStore.preloadSubtestById(0)
    await preloadStore.preloadIntroAssetsStandalone()
    demoProgressKey.value++
    demoAvatarKey.value++
    demoBackgroundKey.value++
    setPausedState(false)
    demoLoading.value = false
  }

  async function playScreenerOutro() {
    demoLoading.value = true
    setPausedState(true)
    effectsStore.resetForScreenerOutro()
    activeScreenerStore.sendSectionChange({ section: 'ScreenerOutro' })
    const lastSubtestIndex = screenerData.value!.pupilQuestionsTemplate!.length - 1
    setCurrentSubtest(lastSubtestIndex)
    const lastQuestionIndex = currentSubtest.value!.questions!.length - 1
    setCurrentQuestion(lastQuestionIndex, false)
    pupilAnswers.value = screenerData.value!.pupilQuestionsTemplate!.flatMap(
      (subtest, subtestIndex) =>
        subtest.questions!.map((question, questionIndex) => ({
          questionId: question.id,
          questionIndex: questionIndex,
          subtestId: subtest.id,
          subtestIndex: subtestIndex,
          practiceAnswer: false,
          selection: null
        }))
    )
    stars.value = pupilAnswers.value.length
    setScreenerCompleted(true)
    effectsStore.setLoadedSceneAnimations(
      screenerData.value?.metadata?.outro?.sceneAnimations || []
    )
    await preloadStore.preloadSubtestById(lastSubtestIndex)

    demoSkipTo.value = 'outro'
    demoProgressKey.value++
    demoAvatarKey.value++
    demoBackgroundKey.value++
    setPausedState(false)
    demoLoading.value = false
  }

  async function playSubtestReward() {
    stars.value = pupilAnswers.value.length

    if (currentSubtest.value) {
      pupilAnswers.value = currentSubtest.value!.questions!.map((question, index) => ({
        questionId: question.id,
        questionIndex: index,
        subtestId: currentSubtest.value!.id,
        subtestIndex: currentSubtest.value!.index,
        practiceAnswer: false,
        selection: null
      }))
      effectsStore.$reset()
      await preloadStore.preloadSubtestById(currentSubtest.value.index)
      setCurrentQuestion(currentSubtest.value!.questions!.length - 1, false)
      resetQuestion()
      demoAvatarKey.value++
    }
  }

  const skipTriggered: Ref<boolean> = ref(false)

  async function skipStep() {
    skipTriggered.value = true
    demoAvatarKey.value++
  }

  function resetSkipTrigger() {
    skipTriggered.value = false
  }

  function setFullscreenElement(element: any) {
    fullscreenElement.value = element
  }

  function clearFullscreenElement() {
    fullscreenElement.value = null
  }

  function resetQuestion() {
    updateQuestionTimestamp(0)
    setScreenerInProgress(false)
    setQuestionAttempts(1)
    demoProgressKey.value++
  }

  function $reset() {
    clearAllLoadErrorTimeouts()
    screenerData.value = null
    screenerTemplateId.value = null
    screenerAvatar.value = null
    pupilQuestions.value = null
    questionsProperty.value = 'practiceQuestions'
    pupilAnswers.value = []
    stars.value = 0
    currentSubtest.value = null
    currentQuestion.value = null
    effectsStore.$reset()
  }

  return {
    screenerData,
    screenerCompleted,
    screenerTemplateId,
    screenerAvatar,
    pupilQuestions,
    pupilAnswers,
    currentSubtest,
    currentQuestion,
    currentQuestionTime,
    currentSubtestAnswers,
    currentSubtestQuestionsLength,
    stars,
    questionAttempts,
    avatarVolumeLevel,
    backgroundVolumeLevel,
    backgroundVolumeFadeLevel,
    screenerError,
    screenerInProgress,
    isOffline,
    pausedState,
    screenerIntroPlayed,
    fullscreenElement,
    starPopGameActive,
    extendedTimeMultiplier,
    answerTimeMultiplier,
    effectiveWaitTime,
    setExtendedTimeMultiplier,

    setStarPopGameState,
    callSetNextQuestion,
    completedCurrentQuestion,
    audioAnswerGiven,
    onSelectAnswer,
    setScreenerError,
    addStar,
    login,
    regenerateCode,
    fetchScreenerData,
    fetchHighlightingTimestamps,
    checkIfLastQuestion,
    checkIfLastSubtest,
    updateQuestionTimestamp,
    setScreenerCompleted,
    setScreenerIntroPlayed,
    setPausedState,
    getNextSubtest,
    setScreenerInProgress,
    startLoadErrorTimeout,
    clearLoadErrorTimeout,
    clearAllLoadErrorTimeouts,
    setFullscreenElement,
    clearFullscreenElement,
    $reset,

    // Demo Controller
    canSkipQuestion,
    canMoveToNextSubtest,
    canMoveToPreviousSubtest,
    isLastQuestionInSubtest,
    isLastSubtest,
    demoProgressKey,
    demoBackgroundKey,
    demoAvatarKey,
    skipTriggered,
    demoSkipTo,
    demoLoading,
    skipQuestion,
    moveToNextSubtest,
    moveToPreviousSubtest,
    resetQuestion,
    playScreenerIntro,
    playScreenerOutro,
    playSubtestReward,
    skipStep,
    resetSkipTrigger,
    resetDemoSkippingTo,
    sendRewardUpdateToApi
  }
})
